import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import useResizeObserver from 'use-resize-observer'
import {Lang} from 'yougile-lang'
import {DataObjectId} from 'yougile-model'
import {LinkButtonNew} from '@components/common/base/buttons'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {TwShowMore} from '@components/my-tasks/tasks-list/containers'
import {updateRef} from '@containers/element-utils'
import {useBoolean, useForceUpdate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {TableBody} from './table-body'
import {TableHead} from './table-head'
import {TableContext, TableStore, useTableStore} from './table-store'
import {PerfectScrollbarWrapperProps, TableDataType, TableProps} from './types'

const TwContainer = tw.div`
  min-w-full
`

export const Table = <T extends TableDataType>(props: TableProps<T>) => {
  const {
    defaultSort,
    rows,
    rowsOnTop,
    columns,
    draggable = false,
    draggableOutside = false,
    headerShadow,
    elementsOnPage,
    showMoreAmount,
    dragType = 'over',
    virtualize,
    onSortApply,
    hoveredRow,
    onRowHover
  } = props

  const [store] = useState(
    () =>
      new TableStore<T>(
        defaultSort,
        rows,
        columns,
        elementsOnPage,
        showMoreAmount,
        dragType,
        virtualize,
        onSortApply,
        onRowHover
      )
  )

  useEffect(
    () => store.updateFromProps(rows, columns, rowsOnTop),
    [rows, columns, rowsOnTop]
  )

  useEffect(() => {
    store.setDraggable(draggable)
  }, [draggable])

  useEffect(() => {
    store.setDraggableOutside(draggableOutside)
  }, [draggableOutside])

  useEffect(() => {
    if (headerShadow !== undefined) {
      store.setHeaderShadow(headerShadow)
    }
  }, [headerShadow])

  useLayoutEffect(() => {
    store.onRowHovered(hoveredRow)
  }, [hoveredRow])

  return (
    <TableContext.Provider value={store}>
      <TableBase {...props} />
    </TableContext.Provider>
  )
}

export const TableBase = observer(
  <T extends TableDataType>(props: TableProps<T>) => {
    const {
      elementsOnPage,
      underHeadElement,
      noFoundElement,
      contentWidth,
      withHeaderShadow,
      shadowType,
      scrollDirection = 'x',
      stripeBackground = false,
      headClassName,
      headClassNameWrapper,
      withDragScroll = false,
      height,
      withPerfectScrollbar = true,
      stickyHeader = true,
      onDragItem,
      noBorders = false,
      onScrollY,
      scrollableRef,
      scrollbarRef,
      prepareDragData,
      onDrop,
      tableRowClassName,
      tableRowInnerClassName,
      isDropBlocked = false,
      highlightRowsAsDropzone = true,
      isTableHeaderHidden,
      forceCustomScroll,
      hideSubRowsOnDrag,
      useNewPlaceholderLine,
      ...rest
    } = props

    const [state, store] = useTableStore()
    const onTop = useBoolean(true)

    useEffect(() => {
      if (props.onChangeDisplayedRows && state.displayedPages) {
        props.onChangeDisplayedRows(state.displayedPages)
      }
    }, [state.displayedPages])

    const onDragStarted = (rowId: DataObjectId) => {
      store.setDraggingRowId(rowId)
    }

    const onDragEnded = () => {
      store.setDraggingRowId(undefined)
      store.isDragTypeOver && onDragItem?.(state.order)
    }

    useResizeObserver({
      ref: state.scrollableEl,
      onResize: store.scheduleVirtualize
    })

    return (
      <PerfectScrollbarWrapper
        withPerfectScrollbar={withPerfectScrollbar}
        scrollDirection={scrollDirection}
        withHeaderShadow={withHeaderShadow}
        height={height}
        withDragScroll={withDragScroll}
        className={rest.className}
        onTop={onTop}
        onScrollY={el => {
          store.scheduleVirtualize()
          onScrollY?.(el)
        }}
        scrollableRef={el => {
          updateRef(el, scrollableRef)
          updateRef(el, store.setScrollableEl)
        }}
        scrollbarRef={x => {
          updateRef(x, scrollbarRef)
          updateRef(x, store.setPerfectScrollbar)
        }}
        forceCustomScroll={forceCustomScroll}
      >
        <TwContainer
          ref={store.setContainerEl}
          style={{width: contentWidth ? `${contentWidth}px` : 'auto'}}
        >
          {!isTableHeaderHidden && (
            <TableHead
              onTop={onTop.value}
              stickyHeader={stickyHeader}
              className={headClassName}
              headClassNameWrapper={headClassNameWrapper}
              shadowType={shadowType}
            />
          )}
          {noFoundElement}
          {underHeadElement}
          <TableBody
            stripeBackground={stripeBackground}
            onDragStarted={onDragStarted}
            onDragEnded={onDragEnded}
            noBorders={noBorders}
            prepareDragData={prepareDragData}
            onDrop={onDrop}
            tableRowClassName={tableRowClassName}
            tableRowInnerClassName={tableRowInnerClassName}
            isDropBlocked={isDropBlocked}
            highlightRowsAsDropzone={highlightRowsAsDropzone}
            hideSubRowsOnDrag={hideSubRowsOnDrag}
            isTableHeaderHidden={isTableHeaderHidden}
            useNewPlaceholderLine={useNewPlaceholderLine}
          />
          {!!elementsOnPage &&
            state.displayedPages &&
            state.allRows.length + (state.rowsOnTop?.length || 0) >
              store.rows.length && (
              <TwShowMore>
                <LinkButtonNew onClick={store.showMore}>
                  {Lang.msg.showMore2}...
                </LinkButtonNew>
              </TwShowMore>
            )}
        </TwContainer>
      </PerfectScrollbarWrapper>
    )
  }
)

const PerfectScrollbarWrapper = observer(
  <T extends TableDataType>({
    withPerfectScrollbar = true,
    scrollDirection,
    withHeaderShadow,
    withDragScroll,
    className,
    onTop,
    children,
    onScrollY,
    scrollableRef,
    scrollbarRef,
    forceCustomScroll
  }: PerfectScrollbarWrapperProps<T>) => {
    const [, store] = useTableStore()

    const forceUpdate = useForceUpdate()
    useEffect(() => {
      // need to show both scrollbars for perfect-scrollbar after initialization
      // updateScroll not works for this case
      setTimeout(() => {
        forceUpdate()
      }, 1000)
    }, [forceUpdate])

    if (!withPerfectScrollbar) {
      return <>{children}</>
    }

    return (
      <WithPerfectScrollbar
        containerRef={el => updateRef(el, scrollableRef)}
        ref={scrollbarRef}
        scrollDirection={scrollDirection}
        onMouseDown={withDragScroll ? store.boardDragScroll : undefined}
        className={cn(className, 'table-content', 'relative')}
        onScrollY={x => {
          const scrollOnTop = x.scrollTop === 0
          onTop.setValue(scrollOnTop)
          if (withHeaderShadow) {
            store.setHeaderShadow(!scrollOnTop)
          }
          onScrollY?.(x)
        }}
        forceCustomScroll={forceCustomScroll}
      >
        {children}
      </WithPerfectScrollbar>
    )
  }
)
